import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Send, Mail, User, MessageSquare, CheckCircle, MapPin, Phone, Clock, Sparkles } from 'lucide-react';
import { PORTFOLIO_DATA } from '../../utils/constants';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Simulate form submission
    setTimeout(() => {
      // Create mailto link with form data
      const subject = encodeURIComponent(formData.subject || `Portfolio Contact from ${formData.name}`);
      const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`);
      const mailtoLink = `mailto:${PORTFOLIO_DATA.email}?subject=${subject}&body=${body}`;
      
      // Open email client
      window.location.href = mailtoLink;
      
      setStatus({ 
        type: 'success', 
        message: 'Email client opened! Your message is ready to send.' 
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      label: 'Email',
      value: PORTFOLIO_DATA.email,
      href: `mailto:${PORTFOLIO_DATA.email}`,
      description: 'Drop me a line anytime'
    },
    {
      icon: <MapPin size={24} />,
      label: 'Location',
      value: 'Kerala, India',
      description: 'Available for remote work globally'
    },
    {
      icon: <Clock size={24} />,
      label: 'Response Time',
      value: '< 24 hours',
      description: 'Usually within a few hours'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  return (
    <section id="contact" className="contact section" ref={ref}>
      <div className="contact-background">
        <div className="contact-mesh">
          <div className="mesh-gradient mesh-1"></div>
          <div className="mesh-gradient mesh-2"></div>
        </div>
        <div className="contact-particles">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="contact-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <motion.div
          className="section-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-badge">
            <Sparkles size={16} />
            <span>Get In Touch</span>
          </motion.div>
          
          <motion.h2 variants={itemVariants} className="section-title">
            Let's Work Together
          </motion.h2>
          
          <motion.p variants={itemVariants} className="section-subtitle">
            Have a project in mind? Let's discuss how we can bring your ideas to life with cutting-edge technology and creative solutions.
          </motion.p>
        </motion.div>
        
        <motion.div
          className="contact-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="contact-info">
            <div className="contact-intro">
              <h3>Ready to start your next project?</h3>
              <p>
                I'm always excited to work on new projects and collaborate with amazing people. 
                Whether you need a full-stack application, custom Odoo solution, or just want to 
                discuss an idea, I'm here to help bring your vision to life.
              </p>
            </div>
            
            <div className="contact-details">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  className="contact-item"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, x: 8 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="contact-item-icon">
                    {item.icon}
                  </div>
                  <div className="contact-item-content">
                    <h4>{item.label}</h4>
                    {item.href ? (
                      <a href={item.href} className="contact-value">
                        {item.value}
                      </a>
                    ) : (
                      <span className="contact-value">{item.value}</span>
                    )}
                    <p className="contact-description">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="contact-availability">
              <div className="availability-status">
                <div className="status-indicator"></div>
                <span>Available for new projects</span>
              </div>
              <p>Currently accepting freelance work and exciting opportunities</p>
            </div>
          </motion.div>
          
          <motion.div variants={itemVariants} className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-header">
                <h3>Send me a message</h3>
                <p>Fill out the form below and I'll get back to you as soon as possible</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <User size={20} />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <Mail size={20} />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <MessageSquare size={20} />
                  </div>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    placeholder="What's this about?"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <div className="input-wrapper">
                  <div className="input-icon textarea-icon">
                    <MessageSquare size={20} />
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell me about your project, ideas, or just say hello!"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
              </div>
              
              {status.message && (
                <motion.div 
                  className={`form-status ${status.type}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <CheckCircle size={16} />
                  {status.message}
                </motion.div>
              )}
              
              <motion.button 
                type="submit" 
                className="btn submit-btn"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;